import { useEffect, useRef, useState, useCallback } from 'react';
import { Input } from '@/components/ui/input';
import { MapPin, Home, Building2, Flag, X, Loader2, Wand2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useMapsLibrary } from '@vis.gl/react-google-maps';

const ICONS = {
  start: MapPin,
  subject: Home,
  address: Building2,
  finish: Flag,
};

const COLORS = {
  start: 'text-[#2563EB]',
  subject: 'text-[#D946EF]',
  address: 'text-[#F59E0B]',
  finish: 'text-[#FF3B30]',
};

const LABELS = {
  start: 'Start Address',
  subject: 'Subject Property',
  address: 'Address',
  finish: 'Finish Address',
};

// Oklahoma City area bounds for autocomplete bias
const OKLAHOMA_BOUNDS = {
  north: 35.8,
  south: 35.1,
  east: -97.0,
  west: -98.0,
};

// Sample Oklahoma addresses for testing - expanded list to avoid duplicates
const SAMPLE_ADDRESSES = {
  start: [
    '1 NW 1st St, Oklahoma City, OK 73102',
    '100 N Broadway Ave, Oklahoma City, OK 73102',
    '201 Robert S Kerr Ave, Oklahoma City, OK 73102',
    '50 N Robinson Ave, Oklahoma City, OK 73102',
  ],
  subject: [
    '2501 NW 23rd St, Oklahoma City, OK 73107',
    '4200 N Western Ave, Oklahoma City, OK 73118',
    '1200 N Walker Ave, Oklahoma City, OK 73103',
    '3000 United Founders Blvd, Oklahoma City, OK 73112',
  ],
  address: [
    '3300 NW 63rd St, Oklahoma City, OK 73116',
    '5900 N May Ave, Oklahoma City, OK 73112',
    '7401 N Pennsylvania Ave, Oklahoma City, OK 73116',
    '2200 SW 59th St, Oklahoma City, OK 73119',
    '1401 NE 23rd St, Oklahoma City, OK 73111',
    '6100 S Western Ave, Oklahoma City, OK 73139',
    '4501 N Lincoln Blvd, Oklahoma City, OK 73105',
    '8200 N Rockwell Ave, Oklahoma City, OK 73132',
    '2609 NW 36th St, Oklahoma City, OK 73112',
    '1100 N Lee Ave, Oklahoma City, OK 73103',
  ],
  finish: [
    '5000 N Classen Blvd, Oklahoma City, OK 73118',
    '2501 W Memorial Rd, Oklahoma City, OK 73134',
    '1901 NW Expressway, Oklahoma City, OK 73118',
    '6001 N Santa Fe Ave, Oklahoma City, OK 73118',
  ],
};

export const AddressInput = ({ 
  type, 
  value, 
  onChange, 
  onRemove, 
  canRemove = false,
  index = null,
  placeholder,
  usedAddresses = [] // New prop to track addresses already in use
}) => {
  const inputRef = useRef(null);
  const containerRef = useRef(null);
  const autocompleteServiceRef = useRef(null);
  const placesServiceRef = useRef(null);
  const sessionTokenRef = useRef(null);
  
  const [inputValue, setInputValue] = useState(value?.address || '');
  const [predictions, setPredictions] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [servicesReady, setServicesReady] = useState(false);

  const placesLib = useMapsLibrary('places');

  const Icon = ICONS[type] || MapPin;
  const color = COLORS[type] || 'text-muted-foreground';
  const label = index !== null ? `${LABELS[type]} ${index}` : LABELS[type];

  // Initialize services when places library is ready
  useEffect(() => {
    if (!placesLib || servicesReady) return;

    try {
      autocompleteServiceRef.current = new placesLib.AutocompleteService();
      placesServiceRef.current = new placesLib.PlacesService(
        document.createElement('div')
      );
      sessionTokenRef.current = new placesLib.AutocompleteSessionToken();
      setServicesReady(true);
    } catch (error) {
      console.error('Error initializing places services:', error);
    }
  }, [placesLib, servicesReady]);

  // Fetch predictions - Oklahoma biased
  const fetchPredictions = useCallback((input) => {
    if (!autocompleteServiceRef.current || !input || input.length < 2) {
      setPredictions([]);
      setShowDropdown(false);
      return;
    }

    setIsLoading(true);
    
    // Add Oklahoma bias to search
    const searchInput = input.toLowerCase().includes('ok') ? input : `${input}, Oklahoma`;
    
    autocompleteServiceRef.current.getPlacePredictions(
      {
        input: searchInput,
        types: ['address'],
        componentRestrictions: { country: 'us' },
        locationBias: {
          north: OKLAHOMA_BOUNDS.north,
          south: OKLAHOMA_BOUNDS.south,
          east: OKLAHOMA_BOUNDS.east,
          west: OKLAHOMA_BOUNDS.west,
        },
        sessionToken: sessionTokenRef.current,
      },
      (results, status) => {
        setIsLoading(false);
        if (status === 'OK' && results) {
          // Filter to Oklahoma results only
          const oklahomaResults = results.filter(r => 
            r.description.includes('OK') || r.description.includes('Oklahoma')
          );
          setPredictions(oklahomaResults.length > 0 ? oklahomaResults : results.slice(0, 3));
          setShowDropdown(true);
          setSelectedIndex(-1);
        } else {
          setPredictions([]);
          setShowDropdown(false);
        }
      }
    );
  }, []);

  // Debounce input changes - faster response (150ms)
  useEffect(() => {
    if (!servicesReady) return;
    
    const timer = setTimeout(() => {
      if (inputValue && inputValue !== value?.address) {
        fetchPredictions(inputValue);
      }
    }, 150);

    return () => clearTimeout(timer);
  }, [inputValue, value?.address, fetchPredictions, servicesReady]);

  // Handle place selection
  const selectPlace = useCallback((prediction) => {
    if (!placesServiceRef.current || !placesLib) return;

    setIsLoading(true);
    setShowDropdown(false);
    
    placesServiceRef.current.getDetails(
      {
        placeId: prediction.place_id,
        fields: ['place_id', 'formatted_address', 'geometry'],
        sessionToken: sessionTokenRef.current,
      },
      (place, status) => {
        setIsLoading(false);
        
        if (status === 'OK' && place) {
          const newValue = {
            address: place.formatted_address,
            placeId: place.place_id,
            location: {
              lat: place.geometry.location.lat(),
              lng: place.geometry.location.lng(),
            },
          };
          setInputValue(place.formatted_address);
          onChange(newValue);
          sessionTokenRef.current = new placesLib.AutocompleteSessionToken();
        }
      }
    );
  }, [onChange, placesLib]);

  // Generate random test address (avoiding already used addresses)
  const generateTestAddress = useCallback(() => {
    const addressList = type === 'address' 
      ? SAMPLE_ADDRESSES.address 
      : SAMPLE_ADDRESSES[type] || SAMPLE_ADDRESSES.start;
    
    // Filter out addresses that are already used
    const availableAddresses = addressList.filter(addr => 
      !usedAddresses.some(used => used && used.includes(addr.split(',')[0]))
    );
    
    // If all addresses are used, fall back to full list
    const finalList = availableAddresses.length > 0 ? availableAddresses : addressList;
    const randomAddress = finalList[Math.floor(Math.random() * finalList.length)];
    setInputValue(randomAddress);
    
    // Trigger autocomplete to get the place details
    if (servicesReady && autocompleteServiceRef.current) {
      autocompleteServiceRef.current.getPlacePredictions(
        {
          input: randomAddress,
          types: ['address'],
          componentRestrictions: { country: 'us' },
          sessionToken: sessionTokenRef.current,
        },
        (results, status) => {
          if (status === 'OK' && results && results.length > 0) {
            selectPlace(results[0]);
          }
        }
      );
    }
  }, [type, servicesReady, selectPlace, usedAddresses]);

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (!showDropdown || predictions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => Math.min(prev + 1, predictions.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => Math.max(prev - 1, 0));
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && predictions[selectedIndex]) {
          selectPlace(predictions[selectedIndex]);
        }
        break;
      case 'Escape':
        setShowDropdown(false);
        setSelectedIndex(-1);
        break;
      default:
        break;
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    setInputValue(value?.address || '');
  }, [value?.address]);

  const handleInputChange = (e) => {
    setInputValue(e.target.value);
    if (!e.target.value) {
      onChange({ address: '', placeId: '', location: null });
      setPredictions([]);
      setShowDropdown(false);
    }
  };

  const handleClear = () => {
    setInputValue('');
    onChange({ address: '', placeId: '', location: null });
    setPredictions([]);
    setShowDropdown(false);
    inputRef.current?.focus();
  };

  return (
    <div 
      ref={containerRef}
      className="space-y-1.5 relative" 
      data-testid={`address-input-${type}${index ? `-${index}` : ''}`}
    >
      <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-2">
        <Icon className={`w-3.5 h-3.5 ${color}`} />
        {label}
      </label>
      <div className="relative flex items-center gap-2">
        <div className="relative flex-1">
          <Input
            ref={inputRef}
            type="text"
            value={inputValue}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onFocus={() => inputValue.length >= 2 && predictions.length > 0 && setShowDropdown(true)}
            placeholder={placeholder || `Enter ${label.toLowerCase()}`}
            className="pr-16 bg-white dark:bg-zinc-900 border-border focus:ring-2 focus:ring-primary/20"
            data-testid={`address-input-field-${type}${index ? `-${index}` : ''}`}
            autoComplete="off"
          />
          <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
            {isLoading ? (
              <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
            ) : (
              <>
                {inputValue && (
                  <button
                    type="button"
                    onClick={handleClear}
                    className="text-muted-foreground hover:text-foreground transition-colors p-1"
                    data-testid={`address-clear-${type}${index ? `-${index}` : ''}`}
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
                <button
                  type="button"
                  onClick={generateTestAddress}
                  className="text-[#2563EB] hover:text-[#2563EB]/80 transition-colors p-1"
                  title="Generate test address"
                  data-testid={`address-generate-${type}${index ? `-${index}` : ''}`}
                >
                  <Wand2 className="w-4 h-4" />
                </button>
              </>
            )}
          </div>
        </div>
        {canRemove && onRemove && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={onRemove}
            className="h-9 w-9 shrink-0 text-muted-foreground hover:text-destructive hover:bg-destructive/10"
            data-testid={`address-remove-${type}${index ? `-${index}` : ''}`}
          >
            <X className="w-4 h-4" />
          </Button>
        )}
      </div>

      {/* Autocomplete Dropdown */}
      {showDropdown && predictions.length > 0 && (
        <div 
          className="absolute left-0 right-0 top-full mt-1 z-50 bg-white dark:bg-zinc-900 border border-border rounded-lg shadow-lg overflow-hidden"
          data-testid={`address-dropdown-${type}${index ? `-${index}` : ''}`}
        >
          {predictions.map((prediction, idx) => (
            <button
              key={prediction.place_id}
              type="button"
              onClick={() => selectPlace(prediction)}
              onMouseEnter={() => setSelectedIndex(idx)}
              className={`w-full px-4 py-3 text-left text-sm transition-colors flex flex-col gap-0.5 ${
                selectedIndex === idx 
                  ? 'bg-muted' 
                  : 'hover:bg-muted/50'
              }`}
              data-testid={`prediction-${idx}`}
            >
              <span className="font-medium text-foreground">
                {prediction.structured_formatting?.main_text || prediction.description}
              </span>
              <span className="text-xs text-muted-foreground">
                {prediction.structured_formatting?.secondary_text || ''}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
